import { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Mail, MessageCircle, Phone, TriangleAlert } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Starter", "Growth", "Scale", "Enterprise", "Not sure yet"];

const CHANNELS = [
    {
        icon: Mail,
        label: "Email us",
        value: "Connect@focs.site",
        href: "mailto:Connect@focs.site",
        testid: "contact-email-link",
    },
    {
        icon: MessageCircle,
        label: "WhatsApp us",
        value: "+91 9180399805",
        href: "https://wa.me/919180399805?text=Hi%20FocS%20%E2%80%94%20I%27d%20like%20to%20discuss%20a%20campaign.",
        testid: "contact-whatsapp-link",
    },
    {
        icon: Phone,
        label: "Call us",
        value: "+91 9180399805",
        href: "tel:+919180399805",
        testid: "contact-phone-link",
    },
];

const inputCls =
    "w-full rounded-xl border border-white/12 bg-white/[0.06] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-crimson/70 focus:bg-white/[0.09]";

export default function Contact() {
    const [status, setStatus] = useState("idle");

    const onSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        setStatus("loading");
        const data = Object.fromEntries(new FormData(form));
        try {
            const res = await axios.post(`${API}/leads`, data);
            if (res.status === 200 || res.status === 201) {
                setStatus("success");
                form.reset();
            } else {
                setStatus("error");
            }
        } catch {
            setStatus("error");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-navy py-16 sm:py-24">
            <div className="hero-glow pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
            <div className="relative mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-10">
                <Reveal>
                    <Eyebrow>Start a campaign</Eyebrow>
                    <h2 className="mt-4 font-syne text-3xl font-bold tracking-tight text-white sm:text-5xl">
                        Let's point attention
                        <br />
                        at your brand.
                    </h2>
                    <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg">
                        Tell us what you're launching and we'll come back with a creator
                        plan and a media plan — from one team.
                    </p>

                    <div className="mt-10 space-y-4">
                        {CHANNELS.map((c) => (
                            <a
                                key={c.testid}
                                href={c.href}
                                target={c.href.startsWith("http") ? "_blank" : undefined}
                                rel="noreferrer"
                                data-testid={c.testid}
                                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-crimson/40 hover:bg-white/[0.08]"
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-crimson to-flame text-white">
                                    <c.icon className="h-5 w-5" />
                                </span>
                                <span>
                                    <span className="block text-xs text-slate-500">{c.label}</span>
                                    <span className="block text-sm font-semibold text-white">
                                        {c.value}
                                    </span>
                                </span>
                                <ArrowRight className="ml-auto h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-crimson" />
                            </a>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur sm:p-9"
                    >
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-1">
                                <label htmlFor="cf-name" className="mb-2 block text-xs font-medium text-slate-400">
                                    Name *
                                </label>
                                <input
                                    id="cf-name"
                                    name="name"
                                    required
                                    data-testid="contact-input-name"
                                    placeholder="Your name"
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label htmlFor="cf-email" className="mb-2 block text-xs font-medium text-slate-400">
                                    Email *
                                </label>
                                <input
                                    id="cf-email"
                                    name="email"
                                    type="email"
                                    required
                                    data-testid="contact-input-email"
                                    placeholder="you@brand.com"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="cf-phone" className="mb-2 block text-xs font-medium text-slate-400">
                                    Phone
                                </label>
                                <input
                                    id="cf-phone"
                                    name="phone"
                                    data-testid="contact-input-phone"
                                    placeholder="+91 ..."
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="cf-company" className="mb-2 block text-xs font-medium text-slate-400">
                                    Company
                                </label>
                                <input
                                    id="cf-company"
                                    name="company"
                                    data-testid="contact-input-company"
                                    placeholder="Brand / company"
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="cf-budget" className="mb-2 block text-xs font-medium text-slate-400">
                                    Budget range
                                </label>
                                <select
                                    id="cf-budget"
                                    name="budget"
                                    data-testid="contact-input-budget"
                                    defaultValue=""
                                    className={`${inputCls} appearance-none`}
                                >
                                    <option value="" disabled>
                                        Select a band
                                    </option>
                                    {BUDGETS.map((b) => (
                                        <option key={b} value={b}>
                                            {b}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="cf-message" className="mb-2 block text-xs font-medium text-slate-400">
                                    Message
                                </label>
                                <textarea
                                    id="cf-message"
                                    name="message"
                                    rows={4}
                                    data-testid="contact-input-message"
                                    placeholder="What are you launching, and where?"
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={status === "loading"}
                            data-testid="contact-form-submit"
                            className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-crimson to-flame px-7 py-4 text-sm font-semibold text-white shadow-xl shadow-crimson/25 transition-all duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {status === "loading" ? "Sending..." : "Send enquiry"}
                            {status !== "loading" && <ArrowRight className="h-4 w-4" />}
                        </button>

                        {status === "success" && (
                            <p
                                data-testid="contact-success-msg"
                                className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-300"
                            >
                                <CheckCircle2 className="h-4 w-4 shrink-0" />
                                Enquiry received — we'll get back to you shortly.
                            </p>
                        )}
                        {status === "error" && (
                            <p
                                data-testid="contact-error-msg"
                                className="mt-4 flex items-center gap-2 rounded-xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm text-red-300"
                            >
                                <TriangleAlert className="h-4 w-4 shrink-0" />
                                Something went wrong. Please email Connect@focs.site instead.
                            </p>
                        )}
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
